import { Row, Col, Typography, Table, Button, message } from "antd";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const MESSAGE_KEY = "SAVE_EARNING_PAYOUTS";

const SaveEarningPayoutsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Save Earning Payouts");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingEarningPayouts,
    status,
    error,
    partnerRoutes,
    saveEarningPayoutsThunk,
    cancelEarningPayoutsThunk,
    removeEarningPayoutsAction,
  } = config;

  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingEarningPayouts;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) dispatch(removeEarningPayoutsAction());
    }
    if (!Object.keys(processingEarningPayouts).length) {
      navigate(`/${partnerRoutes.UPLOAD_EARNING_PAYOUTS}`);
    }
  }, [processingEarningPayouts, navigate, dispatch, removeEarningPayoutsAction, partnerRoutes]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Saving Earning Payouts...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Earning Payouts saved successfully!", duration: 5, key: MESSAGE_KEY });
      navigate(`/${partnerRoutes.EARNING_PAYOUTS}`);
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling Earning Payouts...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
    return () => { message.destroy(); };
  }, [status, error, navigate, partnerRoutes]);

  const { Text } = Typography;
  const { headerMapping } = processingEarningPayouts;
  const headerMappingWithExtras = headerMapping
    ? headerMapping + ",payableEarnings:Payable Earnings,alreadyPaidAmount:Already Paid Amount"
    : undefined;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithExtras),
    [headerMappingWithExtras]
  );

  let content;
  if (!Object.keys(processingEarningPayouts).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const data = processingEarningPayouts.successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = Object.keys(headers).map((key) => ({
      key,
      title: headers[key],
      dataIndex: key,
    }));
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="key"
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={() => dispatch(saveEarningPayoutsThunk())}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save
                    </Button>
                  </Col>
                  <Col style={{ marginLeft: "20px" }}>
                    <Button
                      onClick={() => dispatch(cancelEarningPayoutsThunk())}
                      loading={status === "cancelling"}
                      disabled={status === "loading"}
                      danger
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Earning Payouts" />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};

export default SaveEarningPayoutsPage;
